/**
 * Rate Limiter
 *
 * Client-side rate limiting to prevent API abuse and improve performance.
 * Implements token bucket and sliding window algorithms.
 */

import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

export interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  strategy?: 'token-bucket' | 'sliding-window';
}

export interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  resetAt: number;
  retryAfter?: number;
}

interface RequestRecord {
  timestamp: number;
  key: string;
}

class RateLimiter {
  private requests: Map<string, RequestRecord[]> = new Map();
  private tokens: Map<string, { count: number; lastRefill: number }> = new Map();
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor() {
    if (import.meta.env.MODE !== 'test') {
      this.startCleanup();
    }
  }

  /**
   * Check if request is allowed (Sliding Window)
   */
  checkSlidingWindow(key: string, config: RateLimitConfig): RateLimitResult {
    const now = Date.now();
    const requests = this.requests.get(key) || [];

    // Remove expired requests
    const validRequests = requests.filter((req) => now - req.timestamp < config.windowMs);

    const remaining = Math.max(0, config.maxRequests - validRequests.length);
    const allowed = validRequests.length < config.maxRequests;

    if (allowed) {
      validRequests.push({ timestamp: now, key });
      this.requests.set(key, validRequests);
    }

    const oldestRequest = validRequests[0];
    const resetAt = oldestRequest
      ? oldestRequest.timestamp + config.windowMs
      : now + config.windowMs;

    const retryAfter =
      !allowed && oldestRequest
        ? Math.ceil((oldestRequest.timestamp + config.windowMs - now) / 1000)
        : undefined;

    return {
      allowed,
      remaining,
      resetAt,
      retryAfter,
    };
  }

  /**
   * Check if request is allowed (Token Bucket)
   */
  checkTokenBucket(key: string, config: RateLimitConfig): RateLimitResult {
    const now = Date.now();
    const bucket = this.tokens.get(key) || {
      count: config.maxRequests,
      lastRefill: now,
    };

    // Refill tokens based on time passed
    const timePassed = now - bucket.lastRefill;
    const tokensToAdd = Math.floor((timePassed / config.windowMs) * config.maxRequests);

    if (tokensToAdd > 0) {
      bucket.count = Math.min(config.maxRequests, bucket.count + tokensToAdd);
      bucket.lastRefill = now;
    }

    const allowed = bucket.count > 0;

    if (allowed) {
      bucket.count--;
    }

    this.tokens.set(key, bucket);

    const resetAt = bucket.lastRefill + config.windowMs;
    const retryAfter = !allowed ? Math.ceil((resetAt - now) / 1000) : undefined;

    return {
      allowed,
      remaining: bucket.count,
      resetAt,
      retryAfter,
    };
  }

  /**
   * Check if request is allowed
   */
  check(key: string, config: RateLimitConfig): RateLimitResult {
    const strategy = config.strategy || 'sliding-window';

    if (strategy === 'token-bucket') {
      return this.checkTokenBucket(key, config);
    }

    return this.checkSlidingWindow(key, config);
  }

  /**
   * Attempt request with rate limiting
   */
  async attempt<T>(key: string, config: RateLimitConfig, fn: () => Promise<T>): Promise<T> {
    const result = this.check(key, config);

    if (!result.allowed) {
      throw new RateLimitError(
        `Rate limit exceeded. Try again in ${result.retryAfter} seconds.`,
        result
      );
    }

    return fn();
  }

  /**
   * Reset rate limit for a key
   */
  reset(key: string): void {
    this.requests.delete(key);
    this.tokens.delete(key);
  }

  /**
   * Reset all rate limits
   */
  resetAll(): void {
    this.requests.clear();
    this.tokens.clear();
  }

  /**
   * Get current usage for a key
   */
  getUsage(key: string, config: RateLimitConfig): RateLimitResult {
    const strategy = config.strategy || 'sliding-window';

    if (strategy === 'token-bucket') {
      const bucket = this.tokens.get(key);
      if (!bucket) {
        return {
          allowed: true,
          remaining: config.maxRequests,
          resetAt: Date.now() + config.windowMs,
        };
      }

      return {
        allowed: bucket.count > 0,
        remaining: bucket.count,
        resetAt: bucket.lastRefill + config.windowMs,
      };
    }

    // Sliding window
    const now = Date.now();
    const requests = this.requests.get(key) || [];
    const validRequests = requests.filter((req) => now - req.timestamp < config.windowMs);

    const remaining = Math.max(0, config.maxRequests - validRequests.length);
    const oldestRequest = validRequests[0];
    const resetAt = oldestRequest
      ? oldestRequest.timestamp + config.windowMs
      : now + config.windowMs;

    return {
      allowed: remaining > 0,
      remaining,
      resetAt,
    };
  }

  /**
   * Start cleanup interval to remove old requests
   */
  private startCleanup(): void {
    this.cleanupInterval = setInterval(() => {
      const now = Date.now();

      // Cleanup sliding window requests
      this.requests.forEach((requests, key) => {
        const filtered = requests.filter((req) => now - req.timestamp < 3600000); // 1 hour
        if (filtered.length === 0) {
          this.requests.delete(key);
        } else {
          this.requests.set(key, filtered);
        }
      });

      // Cleanup token buckets
      this.tokens.forEach((bucket, key) => {
        if (now - bucket.lastRefill > 3600000) {
          // 1 hour
          this.tokens.delete(key);
        }
      });
    }, 60000); // Run every minute
  }

  /**
   * Stop cleanup interval
   */
  stopCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }
}

/**
 * Rate Limit Error
 */
export class RateLimitError extends Error {
  constructor(
    message: string,
    public result: RateLimitResult
  ) {
    super(message);
    this.name = 'RateLimitError';
  }
}

/**
 * Rate Limiter Middleware for Axios
 */
export class RateLimiterMiddleware {
  private limiter: RateLimiter;
  private config: RateLimitConfig;

  constructor(config: RateLimitConfig) {
    this.limiter = new RateLimiter();
    this.config = config;
  }

  /**
   * Create request interceptor
   */
  createRequestInterceptor() {
    return async (config: InternalAxiosRequestConfig) => {
      const key = `${config.method}:${config.url}`;
      const result = this.limiter.check(key, this.config);

      if (!result.allowed) {
        throw new RateLimitError(
          `Rate limit exceeded for ${key}. Retry after ${result.retryAfter}s`,
          result
        );
      }

      // Add rate limit headers to request
      config.headers['X-RateLimit-Remaining'] = result.remaining;
      config.headers['X-RateLimit-Reset'] = result.resetAt;

      return config;
    };
  }

  /**
   * Create response interceptor
   */
  createResponseInterceptor() {
    return {
      onFulfilled: (response: AxiosResponse) => {
        if (response.headers['x-ratelimit-remaining']) {
          const data = response.data as Record<string, unknown>;
          data._rateLimit = {
            remaining: parseInt(response.headers['x-ratelimit-remaining']),
            reset: parseInt(response.headers['x-ratelimit-reset']),
          };
        }
        return response;
      },
      onRejected: (error: AxiosError) => {
        // Handle 429 Too Many Requests
        if (error.response?.status === 429) {
          const retryAfter = error.response.headers['retry-after'];
          const resetAt = error.response.headers['x-ratelimit-reset'];

          throw new RateLimitError('Rate limit exceeded from server', {
            allowed: false,
            remaining: 0,
            resetAt: resetAt ? parseInt(resetAt) * 1000 : Date.now() + 60000,
            retryAfter: retryAfter ? parseInt(retryAfter) : 60,
          });
        }
        throw error;
      },
    };
  }
}

// Singleton instance
export const rateLimiter = new RateLimiter();

// Preset configurations
export const RateLimitPresets = {
  // 100 requests per minute
  STANDARD: {
    maxRequests: 100,
    windowMs: 60000,
    strategy: 'sliding-window' as const,
  },

  // 10 requests per minute (strict)
  STRICT: {
    maxRequests: 10,
    windowMs: 60000,
    strategy: 'sliding-window' as const,
  },

  // 1000 requests per minute (lenient)
  LENIENT: {
    maxRequests: 1000,
    windowMs: 60000,
    strategy: 'sliding-window' as const,
  },

  // 5 requests per second (burst)
  BURST: {
    maxRequests: 5,
    windowMs: 1000,
    strategy: 'token-bucket' as const,
  },

  // Authentication endpoints (3 attempts per 15 minutes)
  AUTH: {
    maxRequests: 3,
    windowMs: 900000,
    strategy: 'sliding-window' as const,
  },
};

// Export convenience functions
export const checkRateLimit = (key: string, config: RateLimitConfig) =>
  rateLimiter.check(key, config);

export const attemptRateLimited = <T>(key: string, config: RateLimitConfig, fn: () => Promise<T>) =>
  rateLimiter.attempt(key, config, fn);

export const resetRateLimit = (key: string) => rateLimiter.reset(key);

export const getRateLimitUsage = (key: string, config: RateLimitConfig) =>
  rateLimiter.getUsage(key, config);
